import { useCallback, useEffect, useRef } from 'react';

export type ShareContentKind = 'coupon' | 'delivery';

/**
 * 카카오톡 공유 후 백엔드 웹훅이 도착했는지 확인하는 훅.
 *
 * 백엔드는 웹훅 수신 시 해당 GiftCoupon/GiftDelivery 의
 *   - shareStatus 를 NotShared → Shared 로 승격
 *   - kakaoSharedAt 을 현재 시각으로 세팅
 * 까지 자동 처리합니다.
 *
 * 따라서 프론트는 다음 두 조건이 모두 충족되면 "카톡 공유 성공" 으로 판별합니다.
 *   1) shareStatus !== 'NotShared' (Shared 또는 Viewed)
 *   2) kakaoSharedAt != null
 *
 * 엣지 케이스 (재전송):
 * - 1차 공유 성공 후 사용자가 unshare → shareStatus 가 NotShared 로 리셋됨
 *   (kakaoSharedAt 은 히스토리 목적으로 남아있음)
 * - 다시 카톡 시도했는데 실제론 안 보냄 → shareStatus 는 여전히 NotShared
 *   → 첫 조건 fail → 오탐 없음
 * - 다시 카톡 시도하고 실제로 보냄 → 웹훅이 다시 Shared 로 승격
 *   → 두 조건 모두 만족 → 정상 감지
 */

const INITIAL_DELAY_MS = 0; // 포커스 복귀 즉시 첫 시도
const POLL_INTERVAL_MS = 1000; // 1초 간격
const MAX_POLL_ATTEMPTS = 3; // 최대 3회 ≈ 3초 안에 결과 확정

export interface ShareStateSnapshot {
  /** Shared 또는 Viewed 면 true. NotShared 면 false. */
  isShared: boolean;
  kakaoSharedAt: Date | null;
}

interface UseKakaoShareWebhookCheckOptions {
  /**
   * 카카오 공유 후 백엔드의 현재 공유 상태를 가져오는 콜백.
   * 일반적으로 가벼운 public 단건 조회 (예: GET /public/gift-coupon/:id) 로 구현.
   */
  fetchShareState: (
    kind: ShareContentKind,
    entityId: string,
  ) => Promise<ShareStateSnapshot>;
  onSuccess: (
    kind: ShareContentKind,
    entityId: string,
    snapshot: ShareStateSnapshot,
  ) => void;
  onTimeout: (kind: ShareContentKind, entityId: string) => void;
}

interface ActiveTracker {
  kind: ShareContentKind;
  entityId: string;
  attempts: number;
  aborted: boolean;
  pollTimer: ReturnType<typeof setTimeout> | null;
  initialDelayTimer: ReturnType<typeof setTimeout> | null;
}

export function useKakaoShareWebhookCheck({
  fetchShareState,
  onSuccess,
  onTimeout,
}: UseKakaoShareWebhookCheckOptions) {
  const trackerRef = useRef<ActiveTracker | null>(null);
  const fetchRef = useRef(fetchShareState);
  const onSuccessRef = useRef(onSuccess);
  const onTimeoutRef = useRef(onTimeout);
  fetchRef.current = fetchShareState;
  onSuccessRef.current = onSuccess;
  onTimeoutRef.current = onTimeout;

  const cleanup = useCallback(() => {
    const t = trackerRef.current;
    if (!t) return;
    t.aborted = true;
    if (t.pollTimer) clearTimeout(t.pollTimer);
    if (t.initialDelayTimer) clearTimeout(t.initialDelayTimer);
    trackerRef.current = null;
  }, []);

  const poll = useCallback(() => {
    const t = trackerRef.current;
    if (!t || t.aborted) return;
    t.attempts += 1;

    fetchRef.current(t.kind, t.entityId)
      .then((snapshot) => {
        if (!trackerRef.current || trackerRef.current.aborted) return;

        const succeeded = snapshot.isShared && snapshot.kakaoSharedAt !== null;
        if (succeeded) {
          const { kind, entityId } = t;
          cleanup();
          onSuccessRef.current(kind, entityId, snapshot);
          return;
        }

        if (t.attempts >= MAX_POLL_ATTEMPTS) {
          const { kind, entityId } = t;
          cleanup();
          onTimeoutRef.current(kind, entityId);
          return;
        }

        t.pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      })
      .catch(() => {
        if (!trackerRef.current || trackerRef.current.aborted) return;
        if (t.attempts >= MAX_POLL_ATTEMPTS) {
          const { kind, entityId } = t;
          cleanup();
          onTimeoutRef.current(kind, entityId);
          return;
        }
        t.pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      });
  }, [cleanup]);

  const startPollingOnReturn = useCallback(() => {
    const t = trackerRef.current;
    if (!t || t.aborted) return;
    if (t.initialDelayTimer) clearTimeout(t.initialDelayTimer);
    if (INITIAL_DELAY_MS > 0) {
      t.initialDelayTimer = setTimeout(poll, INITIAL_DELAY_MS);
    } else {
      poll();
    }
  }, [poll]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        startPollingOnReturn();
      }
    };
    const handleFocus = () => {
      startPollingOnReturn();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [startPollingOnReturn]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const start = useCallback(
    (kind: ShareContentKind, entityId: string) => {
      cleanup();
      trackerRef.current = {
        kind,
        entityId,
        attempts: 0,
        aborted: false,
        pollTimer: null,
        initialDelayTimer: null,
      };
    },
    [cleanup],
  );

  const cancel = useCallback(() => {
    cleanup();
  }, [cleanup]);

  return { start, cancel };
}
